import React, { useContext, useEffect, useState } from 'react';
import './Verify.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StoreContext } from '../../Context/StoreContext';
import { useMultiCart } from '../../Context/MultiCartContext';
import axios from 'axios';

const Verify = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { url, token, updateNotificationCount, clearCart } = useContext(StoreContext);
  const { clearAllCarts } = useMultiCart();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState(t('verify.verifying_payment'));

  useEffect(() => {
    let pollInterval = null;
    let pollAttempts = 0;
    const MAX_POLL_ATTEMPTS = 40; // Poll for up to 2 minutes (40 * 3 seconds)
    
    const verifyPayment = async (isPolling = false) => {
      try {
        const currentUrl = window.location.href;
        const orderId = searchParams.get('orderId');
        const success = searchParams.get('success');

        if (!isPolling) {
          console.log('Verify page parameters:', { orderId, success, currentUrl });
        }

        // Check for required parameters
        if (!orderId) {
          console.error("Missing orderId in URL parameters");
          setStatus('error');
          setMessage(t('verify.order_id_missing'));
          return;
        }

        if (!token) {
          console.error("Missing authentication token");
          setStatus('error');
          setMessage(t('verify.authentication_required'));
          return;
        }

        // Handle payment cancellation
        if (success === 'false') {
          setStatus('cancelled');
          setMessage(t('verify.payment_cancelled'));
          setTimeout(() => {
            navigate("/cart");
          }, 3000);
          return;
        }

        // Call verify endpoint
        const response = await axios.post(url + "/api/order/verify", {
          orderId,
          success: success === 'true'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Handle pending payment (waiting for Afripay callback)
        if (response.data.requiresPolling && response.data.paymentStatus === 'pending') {
          if (!isPolling) {
            console.log('Payment pending - starting polling for callback confirmation');
            setStatus('pending');
            setMessage(t('verify.complete_payment_on_phone'));
            
            // Start polling every 3 seconds
            pollInterval = setInterval(() => {
              pollAttempts++;
              console.log(`Polling attempt ${pollAttempts}/${MAX_POLL_ATTEMPTS}`);
              
              if (pollAttempts >= MAX_POLL_ATTEMPTS) {
                clearInterval(pollInterval);
                setStatus('error');
                setMessage(t('verify.payment_timeout'));
                setTimeout(() => {
                  navigate("/cart");
                }, 5000);
                return;
              }
              
              verifyPayment(true);
            }, 3000);
          }
          return;
        }

        // Payment confirmed - stop polling if active
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }

        if (response.data.success) {
          setStatus('success');
          setMessage(t('verify.payment_verified_successfully'));
          
          // Clear cart after successful payment verification
          try {
            // Clear both regular cart and multi-cart
            await Promise.all([
              clearCart(),
              clearAllCarts()
            ]);
            console.log('All carts cleared after successful payment verification');
          } catch (error) {
            console.error('Failed to clear cart after payment verification:', error);
          }
          
          // Update notification count to reflect new active order
          updateNotificationCount(prev => prev + 1);
          
          // Redirect after 3 seconds
          setTimeout(() => {
            navigate("/myorders");
          }, 3000);
        } else {
          setStatus('error');
          setMessage(response.data.message || t('verify.payment_verification_failed'));
          
          // For failed payments, redirect to cart after some time
          setTimeout(() => {
            navigate("/cart");
          }, 5000);
        }
      } catch (error) {
        console.error("Payment verification error:", error);
        
        // Stop polling on error
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
        
        setStatus('error');
        
        if (error.response?.status === 401) {
          setMessage(t('verify.authentication_failed'));
          setTimeout(() => {
            navigate("/");
          }, 3000);
        } else if (error.response?.status === 404) {
          setMessage(t('verify.order_not_found'));
          setTimeout(() => {
            navigate("/cart");
          }, 3000);
        } else {
          setMessage(t('verify.payment_verification_failed_contact_support'));
          setTimeout(() => {
            navigate("/cart");
          }, 5000);
        }
      }
    };

    verifyPayment();
    
    // Cleanup polling on unmount
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [searchParams, token, url, navigate, updateNotificationCount, clearCart, clearAllCarts, t]);

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return '⏳';
      case 'pending':
        return '📱';
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'cancelled':
        return '⚠️';
      default:
        return '⏳';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return '#4CAF50';
      case 'error':
        return '#F44336';
      case 'cancelled':
        return '#FF9800';
      case 'pending':
        return '#FF9800';
      default:
        return '#2196F3';
    }
  };

  const getActionButton = () => {
    switch (status) {
      case 'error':
        return (
          <button 
            onClick={() => navigate("/cart")} 
            className="verify-action-button error"
          >
            {t('verify.go_back_to_cart')}
          </button>
        );
      case 'cancelled':
        return (
          <button 
            onClick={() => navigate("/cart")} 
            className="verify-action-button cancelled"
          >
            {t('verify.try_again')}
          </button>
        );
      case 'success':
        return (
          <button 
            onClick={() => navigate("/myorders")} 
            className="verify-action-button success"
          >
            {t('verify.view_my_orders')}
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="verify">
      <div className="verify-container">
        <div className="status-icon" style={{ color: getStatusColor() }}>
          {getStatusIcon()}
        </div>
        <h2 style={{ color: getStatusColor() }}>
          {status === 'loading' && t('verify.processing_payment')}
          {status === 'pending' && t('verify.complete_payment_title')}
          {status === 'success' && t('verify.payment_successful')}
          {status === 'error' && t('verify.payment_failed')}
          {status === 'cancelled' && t('verify.payment_cancelled_title')}
        </h2>
        <p className="status-message">{message}</p>
        
        {(status === 'loading' || status === 'pending') && (
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        )}
        
        {getActionButton()}
        
        {status === 'success' && (
          <div className="success-details">
            <p>{t('verify.thank_you_order')}</p>
            <p>{t('verify.track_order_my_orders')}</p>
          </div>
        )}
        
        {status === 'error' && (
          <div className="error-details">
            <p>{t('verify.contact_support_if_issues')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Verify;
